/**
 * @title TradeIn Completed Page
 * @description The page shown when the tradein has been completed
 * @image https://storageaccountourimbea2.blob.core.windows.net/tradein2guide/endpage.png
 */
import * as React from "react";
import {FC, useEffect} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {PrivateRenderer} from "../PrivateRenderer";
import {GlobalLayout, StyledButton, StyledDiv, StyledTypography} from "../../../providers/layout/LayoutProvider";
import {makeStyles} from "@mui/styles";
import {ConsumerLabelTradeInFragment, DropOffFragment, useConsumerLabelTradeInQuery} from "../../../Queries";
import {InfoStepper} from "../../../components/InfoStepper";
import {InfoStep} from "../../../components/InfoStep";
import {PackItems, PackItemTradeIn} from "./PackItems";
import {Images} from "../../../Images";
import {Box, CircularProgress, Link} from "@mui/material";
import {Dropoff} from "./Dropoff";
import {HowToDialog} from "./HowTo";
import {useRecoilValue} from "recoil";
import {AccessContextData} from "../../../providers/access/AppContextProvider";
import {LocalizationsAtom} from "../../../providers/localization/LocalizationProvider";
import {ReturnsPagePath} from "../returns/ReturnsPage";
import {LocalizationText} from "../../../providers/localization/LocalizationText";

export const TradeInPagePath = (id?: string) => {
   if (id) {
      return ReturnsPagePath() + '/tradein/' + id
   } else {
      return ReturnsPagePath() + '/tradein'
   }
}

export const TradeInPage: FC<any> = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const {tradeInId} = useParams<{ tradeInId: string }>();
   const classes = useStyles();
   const navigate = useNavigate();
   const location = useLocation();
   const appContext = useRecoilValue(AccessContextData);
   const globalLayout = useRecoilValue(GlobalLayout);
   const [showHowTo, setShowHowTo] = React.useState<boolean>(false);
   const [showDropoff, setShowDropoff] = React.useState<boolean>(false);
   const [delayed, setDelayed] = React.useState<boolean>(false);

   const {data, stopPolling} = useConsumerLabelTradeInQuery({
      variables: {
         tradeInId: tradeInId
      },
      fetchPolicy: "no-cache",
      pollInterval: 4000,
      notifyOnNetworkStatusChange: true
   })

   const tradeIn = data?.TradeInById as ConsumerLabelTradeInFragment;
   const showShops = location.pathname.endsWith('shops');
   const showTerms = location.pathname.endsWith('terms');

   // Unhide tradeinroot when showShops is true
   useEffect(() => {
      if (showShops) {
         const tradeInRoot = document.getElementById('tradeinroot');
         if (tradeInRoot && tradeInRoot.style.display === 'none') {
            tradeInRoot.style.display = 'block';
         }
      }
   }, [showShops]);

   useEffect(() => {
      if (!isLabelReady(tradeIn) && tradeIn?.label?.createdAt) {
         const diff = new Date().getTime() - new Date(tradeIn?.label?.createdAt).getTime()
         const seconds = Math.abs(diff) / 1000
         setDelayed(seconds > 60)
      } else {
         setDelayed(false);
      }
      if (isLabelReady(tradeIn) && isVoucherReady(tradeIn)) {
         stopPolling();
      }
   }, [data, stopPolling, tradeIn])

   const onShowPDF = () => {
      if (tradeIn?.label?.url) {
         window.open(tradeIn!.label!.url, '_blank');
      }
   }

   const onShowQR = (e: any) => {
      if (tradeIn?.label?.qrUrl) {
         window.open(tradeIn!.label!.qrUrl, '_blank');
      }
   }

   const onNavigateTo = (url?: string) => () => {
      if (appContext?.pos) {
         navigate('/pos');
      } else if (url) {
         window.location.replace(url);
      } else {
         navigate(ReturnsPagePath());
      }
   }

   const onHideDialog = () => {
      setShowHowTo(false);
   };
   const dropOffPoints = (tradeIn?.dropOffPoints || []) as DropOffFragment[];

   if (showShops) {
      return <Dropoff dropOffs={dropOffPoints}
                      showQr={!!tradeIn?.label?.qrUrl}
                      open={true} onHide={() => {
         setShowDropoff(false);
      }}/>
   }
   
   // Terms page handling is now in TermsPage.tsx, this check is just for backward compatibility
   if (showTerms) {
      navigate(-1); 
      return null;
   }

   let isStandardShipping = (tradeIn?.shippingCarrier?.carrierId === 'shipmondo' ||
      tradeIn?.shippingCarrier?.carrierId === 'carriergateway') && !tradeIn?.shippingCarrier?.customCarrier;
   let isPickupShipping = tradeIn?.shippingCarrier?.carrierId === 'pickup' || tradeIn?.shippingCarrier?.customCarrier;
   const includesMergeFields = localizations.consumerAlmostThereCreditDesc?.includes('{{');
   const voucherReady = isVoucherReady(tradeIn);

   return <PrivateRenderer step={"tradein"} hideSteppers={true}>
      <StyledDiv type={"main"} className={classes.page}>
         <div className={classes.pageTitle}>
            <StyledTypography variant={"h1"}>
               {localizations.tradeInTitle}
            </StyledTypography>
            <StyledTypography variant={"body1"}>
               {localizations.tradeInDescription}
            </StyledTypography>
         </div>
         <HowToDialog onHideDialog={onHideDialog} open={showHowTo} code={!!tradeIn?.label?.labelCode}
                      qr={!!tradeIn?.label?.qrUrl}/>
         <div className={classes.labelContainer}>
            {isTradeInReady(tradeIn) && <>
               <InfoStepper>
                  {isStandardShipping && <>
                     <InfoStep icon={globalLayout.disableIcons ? "1" : (localizations.consumerAlmostThereStep1Image || Images.PACKIT)}>
                        <LocalizationText
                           text={localizations.consumerAlmostTherePackIt}
                           typographyProps={{variant: "h2", className: classes.titleSpacing}}
                           context={tradeIn}
                        />
                        <PackItems tradeIn={tradeIn as PackItemTradeIn}/>
                        <LocalizationText
                           text={localizations.consumerAlmostTherePackItDesc}
                           typographyProps={{variant: "body1"}}
                           context={tradeIn}
                        />

                        <div className={classes.packagingCode}>
                           {(tradeIn.label?.labelFailed) && <>
                              <div className={classes.packCode}>
                                 {tradeIn.label.labelFailed && <div>Label generation failed</div>}
                                 Please contact our support hotline
                              </div>
                           </>}
                           {!tradeIn.label?.labelFailed && <>

                              {tradeIn.label?.labelCode && <>
                                 <LocalizationText
                                    text={localizations.consumerAlmostTherePackItCode}
                                    typographyProps={{variant: "body1"}}
                                    context={tradeIn}
                                 />
                                 <LocalizationText
                                    text={tradeIn.label.labelCode}
                                    typographyProps={{variant: "h2"}}
                                    context={tradeIn}
                                 />
                              </>
                              }

                              {!tradeIn.label?.labelCode && tradeIn.label?.qrUrl && <>
                                 <LocalizationText
                                    text={localizations.consumerAlmostTherePackItQR}
                                    typographyProps={{variant: "body1"}}
                                    context={tradeIn}
                                 />
                                 <StyledButton sx={{minWidth: '150px'}} styletype={"main"} variant={"outlined"}
                                               onClick={onShowQR}>
                                    {localizations.consumerAlmostTherePackItLabelButton}
                                 </StyledButton>
                              </>
                              }

                              {!tradeIn.label?.labelCode && tradeIn.label?.url && <>
                                 <LocalizationText
                                    text={localizations.consumerAlmostTherePackItLabel}
                                    typographyProps={{variant: "body1", sx: {marginTop: '20px'}}}
                                    context={tradeIn}
                                 />
                                 <StyledButton sx={{minWidth: '150px'}} styletype={"main"} variant={"outlined"}
                                               onClick={onShowPDF}>
                                    {localizations.consumerAlmostTherePackItLabelButton}
                                 </StyledButton>
                              </>
                              }

                              {!isLabelReady(tradeIn) && <>
                                 {delayed && <>
                                    <div style={{padding: '10px', marginLeft: '-10px', border: '1px solid #c00000'}}>
                                       <LocalizationText
                                          text={localizations.consumerAlmostThereDelayedLine1}
                                          typographyProps={{variant: "body1"}}
                                          context={tradeIn}
                                       /><br/>
                                       <LocalizationText
                                          text={localizations.consumerAlmostThereDelayedLine2}
                                          typographyProps={{variant: "body1"}}
                                          context={tradeIn}
                                       />
                                    </div>
                                 </>}
                                 {!delayed && <>
                                    <LocalizationText
                                       text={localizations.consumerAlmostTherePleaseWait}
                                       typographyProps={{variant: "body1"}}
                                       context={tradeIn}
                                    /><br/>
                                    <CircularProgress/>
                                 </>}
                              </>}

                              {isLabelReady(tradeIn) &&
                                 <div className={classes.packCodeLink}>
                                    <Link style={{color: globalLayout.mainPanel?.textColor || '#000000'}} href={"#"}
                                          onClick={(e: any) => {
                                             e.stopPropagation();
                                             e.preventDefault();
                                             setShowHowTo(true)
                                          }}>
                                       <LocalizationText
                                          text={localizations.consumerAlmostTherePackItHowTo}
                                          typographyProps={{variant: "body1"}}
                                          context={tradeIn}
                                       />
                                    </Link>
                                 </div>}
                           </>}
                        </div>
                     </InfoStep>
                     <InfoStep icon={globalLayout.disableIcons ? "2" : (localizations.consumerAlmostThereStep2Image || Images.DROPIT)}>
                        <LocalizationText
                           text={localizations.consumerAlmostThereDropOff}
                           typographyProps={{variant: "h2", className: classes.titleSpacing}}
                           context={tradeIn}
                        />
                        {dropOffPoints.length > 0 &&
                           <LocalizationText
                              text={localizations.consumerAlmostThereDropOffDesc}
                              typographyProps={{variant: "body1"}}
                              context={tradeIn}
                           />}
                        {dropOffPoints.length === 0 &&
                           <LocalizationText
                              text={localizations.consumerAlmostThereDropOffDescNoDrops}
                              typographyProps={{variant: "body1"}}
                              context={tradeIn}
                           />}
                        {dropOffPoints.length > 0 &&
                           <div className={classes.findNearest}>
                              <Link style={{color: globalLayout.mainPanel?.textColor || '#000000'}} href={"#"}
                                    onClick={(e: any) => {
                                       e.stopPropagation();
                                       e.preventDefault();
                                       //GoogleAnalytics.event('ShowDropoff', 'Freight Reload');
                                       setShowDropoff(true)
                                    }}>
                                 <LocalizationText
                                    text={localizations.consumerAlmostThereDropOffFindNearest}
                                    typographyProps={{variant: "body1"}}
                                    context={tradeIn}
                                 />
                              </Link>
                              <Dropoff dropOffs={dropOffPoints}
                                       showQr={!!tradeIn.label?.qrUrl}
                                       open={showDropoff} onHide={() => {
                                 setShowDropoff(false);
                              }}/>
                           </div>}

                     </InfoStep>
                     <InfoStep icon={globalLayout.disableIcons ? "3" : (localizations.consumerAlmostThereStep3Image || Images.CREDIT)}>
                        <LocalizationText text={localizations.consumerAlmostThereCredit}
                                          typographyProps={{variant: "h2"}}
                                          context={tradeIn?.vouchers?.[0]}
                                          className={classes.titleSpacing}
                        />
                        {(voucherReady || !includesMergeFields) &&
                           <LocalizationText text={localizations.consumerAlmostThereCreditDesc}
                                             typographyProps={{variant: "body1"}}
                                             context={tradeIn?.vouchers?.[0]}
                                             style={{userSelect: 'text'}}
                           />
                        }
                        {(!voucherReady && includesMergeFields) &&
                           <CircularProgress/>
                        }
                     </InfoStep>
                  </>}
                  {isPickupShipping && <>
                     <InfoStep icon={globalLayout.disableIcons ? "1" : (localizations.consumerAlmostThereStep1Image || Images.PACKIT)}>
                        <StyledTypography
                           variant={"h2"}>{localizations.consumerAlmostTherePackIt}</StyledTypography>
                        <PackItems tradeIn={tradeIn as PackItemTradeIn}/>
                        <StyledTypography
                           variant={"body1"}>{localizations.consumerAlmostTherePickupPackItDesc}</StyledTypography>
                     </InfoStep>
                     <InfoStep icon={globalLayout.disableIcons ? "2" : (localizations.consumerAlmostThereStep2Image || Images.PICKUP)}>
                        <StyledTypography
                           variant={"h2"}>{localizations.consumerAlmostTherePickup2}</StyledTypography>
                        <StyledTypography
                           variant={"body1"} dangerouslySetInnerHTML={{
                           __html: localizations.consumerAlmostTherePickupDesc2
                        }}/>
                     </InfoStep>
                     <InfoStep icon={globalLayout.disableIcons ? "3" : (localizations.consumerAlmostThereStep3Image || Images.CREDIT)}>
                        <StyledTypography
                           variant={"h2"}>{localizations.consumerAlmostThereCredit}</StyledTypography>
                        <StyledTypography
                           variant={"body1"} dangerouslySetInnerHTML={{
                           __html: localizations.consumerAlmostThereCreditDesc
                        }}/>
                     </InfoStep>
                  </>}
               </InfoStepper>
               <LocalizationText text={localizations.consumerAlmostThereNoticeMail}
                                 typographyProps={{variant: "body1"}}
                                 context={tradeIn}
               />
               <Box sx={{display: 'flex', gap: '10px'}}>
                  {localizations.consumerAlmostTHereBackToStart &&
                     <StyledButton styletype={"main"} style={{marginTop: '20px', padding: '5px 40px 5px 40px'}}
                                   onClick={onNavigateTo(localizations.consumerAlmostThereBackToStartUrl)}>
                        {localizations.consumerAlmostTHereBackToStart}
                     </StyledButton>
                  }
                  {localizations.consumerAlmostThereCTA1 &&
                     <StyledButton styletype={"main"} style={{marginTop: '20px', padding: '5px 40px 5px 40px'}}
                                   onClick={onNavigateTo(localizations.consumerAlmostThereCTA1Url)}>
                        {localizations.consumerAlmostThereCTA1}
                     </StyledButton>
                  }
                  {localizations.consumerAlmostThereCTA2 &&
                     <StyledButton styletype={"main"} style={{marginTop: '20px', padding: '5px 40px 5px 40px'}}
                                   onClick={onNavigateTo(localizations.consumerAlmostThereCTA2Url)}>
                        {localizations.consumerAlmostThereCTA2}
                     </StyledButton>
                  }
               </Box>
            </>
            }
         </div>
      </StyledDiv>
   </PrivateRenderer>
}

const useStyles = makeStyles({
   page: {
      display: 'flex',
      width: '100%',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'start',
      marginTop: '50px'
   },
   titleSpacing: {
      marginTop: '-5px',
      marginBottom: '20px'
   },
   labelContainer: {
      width: '600px'
   },
   pageTitle: {
      width: '440px',
      textAlign: 'center',
      marginBottom: '40px'
   },
   '@media only screen and (max-width: 800px)': {
      pageTitle: {
         width: '90%',
         maxWidth: '440px'
      },
      profileContainer: {
         width: '80%'
      },
      centeredText: {
         width: '85%'
      },
      paddedBox: {
         width: '85%',
      },
      labelContainer: {
         width: '90%',
      }
   },
   totalLine: {
      float: 'right',
      marginRight: '15px',
      fontWeight: 'bold',
      textAlign: 'right'
   },
   greyBox: {
      backgroundColor: '#EDEDED',
      color: '#000000',
      width: '100%',
      paddingTop: '39px',
      paddingBottom: '39px',
   },
   centeredText: {
      textAlign: 'center',
      paddingLeft: '20px',
      paddingRight: '20px',
      margin: '0 auto',
      width: '800px'
   },
   paddedBox: {
      margin: '0 auto',
      width: '800px',
      paddingLeft: '20px',
      paddingRight: '20px',
      paddingTop: '40px'
   },
   stepContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
   },
   cartImage: {
      width: '75px'
   },
   textButton: {
      position: 'absolute',
      backgroundColor: 'transparent',
      left: '0',
      bottom: '0',
   },
   backButton: {
      marginTop: '20px',
      fontWeight: 'bold'
   },
   creditBox: {
      backgroundColor: '#F4F4F4',
      display: 'inline-block',
      paddingLeft: '20px',
      paddingRight: '20px',
      paddingTop: '10px',
      paddingBottom: '10px',
      position: 'absolute',
      right: '0',
      bottom: '0',
      fontWeight: 'bold'
   },
   bottomPanel: {
      position: 'relative',
      height: '40px'
   },
   packagingCode: {
      marginTop: '23px'
   },
   packCodeLink: {
      marginTop: '20px'
   },
   packCode: {
      backgroundColor: '#f4f4f4'
   },
   findNearest: {
      marginTop: '23px'
   },
   buttonBackToStart: {
      marginTop: '50px',
      color: 'white'
   },
   box: {
      marginBottom: '20px',
   },
   boxImage: {
      textAlign: 'center'
   },
   labelCode: {
      fontSize: '20px',
      fontWeight: 600,
   },
   productContainer: {
      display: 'flex',
      flexWrap: 'wrap'
   }
});

const isTradeInReady = (tradeIn?: ConsumerLabelTradeInFragment | null) => {
   if (tradeIn && tradeIn.label) {
      return true;
   } else {
      return !!tradeIn
   }
}

const isLabelReady = (tradeIn?: ConsumerLabelTradeInFragment | null) => {
   return isTradeInReady(tradeIn) && (tradeIn?.label?.url || tradeIn?.label?.labelFailed || tradeIn?.label?.labelCode);
}

const isVoucherReady = (tradeIn?: ConsumerLabelTradeInFragment | null) => {
   return !!(tradeIn && (tradeIn.vouchers || []).length > 0);
}
